import React, { useState } from 'react';
import { Shield, Lock, FileText, UserCheck, Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';

export const AvisoPrivacidad = () => {
  const [accepted, setAccepted] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const sections = [
    { id: 'general', title: '1. Identidad y Responsabilidad', icon: Shield },
    { id: 'datos', title: '2. Datos Personales y Sensibles Recabados', icon: FileText },
    { id: 'finalidades', title: '3. Finalidades del Tratamiento', icon: UserCheck },
    { id: 'transferencias', title: '4. Transferencia de Datos', icon: Lock },
    { id: 'arco', title: '5. Derechos ARCO', icon: FileText },
    { id: 'seguridad', title: '6. Medidas de Seguridad', icon: Shield },
    { id: 'contacto', title: '7. Contacto y Actualizaciones', icon: Phone },
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-teal-600 p-2 rounded-lg text-white">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <span className="font-bold text-lg text-slate-900">Consultorio Digital</span>
              <span className="text-xs block text-slate-500">Aviso de Privacidad Integral</span>
            </div>
          </div>
          <div className="text-sm text-slate-500">
            Última actualización: <span className="font-medium text-slate-700">Octubre 2026</span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-grow w-full grid grid-cols-1 lg:grid-cols-4 gap-8">
        
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block lg:col-span-1">
          <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3 px-2">
              Contenido
            </h3>
            <nav className="space-y-1">
              {sections.map((sec) => {
                const Icon = sec.icon;
                const isActive = activeSection === sec.id;
                return (
                  <button
                    key={sec.id}
                    onClick={() => scrollToSection(sec.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-teal-600' : 'text-slate-400'}`} />
                    <span className="text-left truncate">{sec.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Content Body */}
        <main className="lg:col-span-3 space-y-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10 space-y-10">
            
            {/* Intro */}
            <div className="border-b border-slate-100 pb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Aviso de Privacidad para Aplicaciones y Plataformas Médicas
              </h1>
              <p className="text-slate-600 leading-relaxed">
                En cumplimiento con las leyes de protección de datos personales en posesión de particulares, 
                <strong> Consultorio Digital</strong> asume el compromiso de proteger su privacidad, garantizando la 
                confidencialidad, integridad y el manejo seguro de su información médica y personal.
              </p>
            </div>

            {/* Section 1 */}
            <section id="general" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">1.</span> Identidad y Domicilio del Responsable
              </h2>
              <p className="text-slate-600 leading-relaxed">
                <strong>Consultorio Digital</strong> (en adelante, la "Plataforma"), es responsable del tratamiento y protección 
                de los datos personales que usted nos proporciona. Nuestro domicilio convencional para los efectos de este aviso 
                se ubica en el territorio nacional. Para cualquier duda o comentario respecto al presente aviso, puede contactarnos 
                a través del correo electrónico: <a href="mailto:privacidad@consultoriodigital.com" className="text-teal-600 hover:underline">privacidad@consultoriodigital.com</a>.
              </p>
            </section>

            {/* Section 2 */}
            <section id="datos" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">2.</span> Datos Personales y Datos Sensibles que se Recaban
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Para cumplir con las finalidades previstas en este Aviso de Privacidad, podremos recabar las siguientes categorías de datos:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li><strong>Datos de Identificación y Contacto:</strong> Nombre completo, teléfono, correo electrónico, fecha de nacimiento y domicilio.</li>
                <li><strong>Datos de Salud (Sensibles):</strong> Historial clínico, antecedentes médicos, padecimientos actuales, diagnósticos, tratamientos, recetas médicas, signos vitales y resultados de laboratorio.</li>
                <li><strong>Datos Financieros y de Facturación:</strong> Datos bancarios necesarios para el procesamiento de pagos de consultas o servicios dentro de la plataforma.</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="finalidades" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">3.</span> Finalidades del Tratamiento de los Datos
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Los datos personales y de salud que recabamos serán utilizados para las siguientes finalidades necesarias para la prestación del servicio:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600">
                <li>Creación y gestión de su expediente clínico electrónico y perfil de usuario.</li>
                <li>Agendamiento, confirmación y recordatorios de citas médicas.</li>
                <li>Brindar atención médica, diagnóstico y seguimiento por parte de profesionales de la salud autorizados.</li>
                <li>Emisión de recetas electrónicas, órdenes de estudio y comprobantes de pago o facturación.</li>
                <li>Cumplimiento de normativas sanitarias vigentes en materia de expedientes clínicos y salud digital.</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="transferencias" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">4.</span> Transferencia de Datos Personales
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Sus datos personales de salud podrán ser transferidos exclusivamente a médicos especialistas, laboratorios o instituciones 
                hospitalarias directamente involucradas en su atención médica, previo su consentimiento o cuando sea estrictamente 
                necesario para salvaguardar su salud en situaciones de emergencia. No comercializamos ni rentamos su información con terceros.
              </p>
            </section>

            {/* Section 5 */}
            <section id="arco" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">5.</span> Derechos ARCO (Acceso, Rectificación, Cancelación y Oposición)
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Usted tiene derecho a conocer qué datos tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos (Acceso). 
                Asimismo, es su derecho solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o 
                incompleta (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo 
                utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos para fines específicos (Oposición).
              </p>
              <p className="text-slate-600 leading-relaxed">
                Para el ejercicio de cualquiera de los derechos ARCO, puede enviar una solicitud formal a nuestro departamento de privacidad 
                a través de <a href="mailto:privacidad@consultoriodigital.com" className="text-teal-600 hover:underline">privacidad@consultoriodigital.com</a>.
              </p>
            </section>

            {/* Section 6 */}
            <section id="seguridad" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">6.</span> Medidas de Seguridad de la Información
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Implementamos estrictas medidas de seguridad administrativas, técnicas y físicas, incluyendo cifrado de datos (SSL/TLS), 
                controles de acceso estrictos basados en roles y respaldos seguros en servidores certificados, para proteger su información 
                médica contra daño, pérdida, alteración, destrucción o uso no autorizado.
              </p>
            </section>

            {/* Section 7 */}
            <section id="contacto" className="scroll-mt-28 space-y-3">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span className="text-teal-600">7.</span> Cambios al Aviso de Privacidad
              </h2>
              <p className="text-slate-600 leading-relaxed">
                El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos 
                legales, de nuestras propias necesidades por los productos o servicios que ofrecemos, o por prácticas de privacidad. 
                Cualquier modificación le será notificada a través de nuestra plataforma web o aplicación móvil.
              </p>
            </section>

            {/* Footer Acceptance Box */}
            <div className="mt-12 pt-8 border-t border-slate-200 bg-teal-50/50 -mx-6 -mb-6 sm:-mx-10 sm:-mb-10 p-6 sm:p-8 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="accept"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="w-5 h-5 text-teal-600 border-slate-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="accept" className="text-sm font-medium text-slate-700 cursor-pointer">
                    He leído y comprendido los términos del Aviso de Privacidad y consiento el tratamiento de mis datos de salud.
                  </label>
                </div>
                <button
                  disabled={!accepted}
                  onClick={() => alert('¡Gracias por aceptar el aviso de privacidad!')}
                  className={`px-6 py-2.5 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                    accepted
                      ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20 cursor-pointer'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Continuar
                </button>
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between text-sm text-slate-500 gap-4">
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <span>México</span>
          </div>
          <div className="flex space-x-6">
            <a href="#contacto" className="hover:text-slate-900 transition-colors flex items-center gap-1">
              <Mail className="w-4 h-4" /> Soporte y Privacidad
            </a>
            <a href="#contacto" className="hover:text-slate-900 transition-colors flex items-center gap-1">
              <Phone className="w-4 h-4" /> Contacto Médico
            </a>
          </div>
          <div>
            &copy; {new Date().getFullYear()} Consultorio Digital. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}